import {
  Component,
  ChangeDetectorRef,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

import clone from 'lodash-es/clone';
import cloneDeep from 'lodash-es/cloneDeep';
import isEmpty from 'lodash-es/isEmpty';
import { Property } from 'app/modules/projects/projects.types';
import { PanelsInterfaceComponent } from '../panels-interface.component';
import { PartialFormField } from 'app/shared/components/form-fields/form-field.types';
import { AccountService } from 'app/core';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { Account, FieldDef } from 'app/core/models';
import { PropertyUnit } from 'app/modules/projects/details/tenants/tenants.types';
import { PropertyTenantsService } from '../../../tenants/tenants.service';
import { getAllowedFields } from 'app/shared/functions';
import { RelatedToType } from 'app/shared/enums';
import { PanelDef } from 'app/shared/components/panels/panels.types';
@Component({
  selector: 'property-panel-unit-form',
  templateUrl: './unit-form.component.html',
  styleUrls: ['./unit-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsUnitFormComponent implements PanelsInterfaceComponent, OnInit {
  @Input() property: Property;
  @Input() account: Account;
  @Input() readonly = false;
  @Input() panelDef: PanelDef<any>;
  @Input() data: any;
  @Input() canEditPanelDef: boolean = true;
  @Output() onUpdate: EventEmitter<PartialFormField> = new EventEmitter();
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();

  private readonly destroyRef = inject(DestroyRef);

  defaultFieldDefs: FieldDef[] = [];
  titleFieldDef: any;
  panelName: string;
  changeTitleMode: boolean = false;

  public editMode: boolean = false;
  public fields: any[] = [];
  public fieldsCombined: any[] = [];
  private _originalFields: any[] = [];
  dataOrig: any;

  fieldDefs = [];
  unit: any;
  units: any = [];
  rowActionDisabled = [];

  constructor(
    private readonly _propertiesService: PropertiesService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _accountService: AccountService,
    private readonly _propertyTenantsService: PropertyTenantsService,
  ) {}

  ngOnInit() {
    this.cleanPanels();
    this.fields = clone(this.panelDef.meta.fields);
    this._originalFields = clone(this.panelDef.meta.fields);
    this.titleFieldDef = {
      name: 'panel_name',
      label: this.panelDef.title,
      type: 'text',
    };
    this.units = cloneDeep(this.data.unit.property_units);
    if (this.units?.length > 0) {
      this.cleanUnit(this.data.unit.property_units[0]);
    }

    this.fieldDefs = getAllowedFields(this.data?.fieldDefs, RelatedToType.PropertyUnit);
    this.dataOrig = cloneDeep(this.data);
    this.dataOrig.fieldDefs = this.fieldDefs;
    this._propertiesService.property$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((property: Property) => {
      if (property) {
        this.property = property;
      }

      this._changeDetectorRef.markForCheck();
    });

    this._propertyTenantsService.propertyUnits$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((units: PropertyUnit[]) => {
        this.units = cloneDeep(units);
        if (this.units?.length > 0) {
          if (this.unit === undefined || this.units.find((unit) => unit.id === this.unit.id) === undefined)
            this.unit = cloneDeep(this.units[0]);
        } else this.unit = undefined;

        this._changeDetectorRef.markForCheck();
      });

    this._accountService.defaultFieldDefs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fields) => {
      this.defaultFieldDefs = getAllowedFields(fields, RelatedToType.PropertyUnit);
    });

    this._accountService.fieldDefs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fields) => {
      this.fieldDefs = getAllowedFields(fields, RelatedToType.PropertyUnit);
    });
  }

  cleanUnit(unit) {
    this.unit = unit;

    unit.custom_field_values.forEach((customField) => {
      unit[customField.name] = customField.val;
    });
  }

  cleanPanels() {
    let cleanFields = [];
    this.panelDef.meta.fields.forEach((field) => {
      let cleanField = field.filter(function (e) {
        return e;
      });
      if (cleanField.length > 0)
        cleanFields.push(
          field.filter(function (e) {
            return e;
          }),
        );
    });
    this.panelDef.meta.fields = cleanFields;
    this.fields = clone(this.panelDef.meta.fields);
  }

  dropped(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  toggleEditMode(editMode: boolean | null = null): void {
    this.cleanPanels();
    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }
    this.changeTitleMode = false;

    this._changeDetectorRef.markForCheck();
  }

  cancelEdits(): void {
    this.fields = this._originalFields;

    this.panelDef.meta.fields = clone(this._originalFields);

    this.toggleEditMode(false);
  }

  toggleChangeTitleMode() {
    this.changeTitleMode = true;
  }

  saveLayout(): void {
    this.panelDef.meta.fields = this.fields.filter((row) => row && row.length > 0);
    if (this.panelName) {
      this.panelDef.title = this.panelName;
    }
    this.onEditPanelDef.emit(this.panelDef);
    this.toggleEditMode(false);
  }

  onFieldSelectionChange($event, row, col): void {
    this.fields[row][col] = $event?.value || $event?.id || this.fields[row][col];
  }

  removeField(row, col): void {
    this.fields[row].splice(col, 1);
    if (isEmpty(this.fields[row])) {
      this.fields.splice(row, 1);
    }
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Add a field to the last column in a row
   */
  addField(row, lastCol): void {
    const defaultField = this.getUnitNumberFieldOrFirstValid();
    this.fields[row].push(defaultField);
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Add a new row and initialize a col
   */
  addRow(index): void {
    this.dataOrig.fieldDefs = this.fieldDefs;
    if (this.fields.length > 0) {
      const defaultField = this.getUnitNumberFieldOrFirstValid();
      this.fields.splice(index + 1, 0, [defaultField]);
      this.panelDef.meta.fields.splice(index + 1, 0, [defaultField]);
    } else {
      this.fields.push(['unit_number']);
      this.panelDef.meta.fields.push(['unit_number']);
    }
    this._changeDetectorRef.markForCheck();
  }

  update(params) {
    let currentKey = Object.keys(params)[0];
    if (currentKey === 'panel_name') {
      this.panelName = params.panel_name;
    } else {
      const selectColumn = this.defaultFieldDefs.map((m) => m.name) || [];
      selectColumn.push(currentKey);
      selectColumn.push('custom_field_values');
      this._propertyTenantsService.updatePropertyUnit(this.unit).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  lookupField(name) {
    let fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }

  chunk(array, size): any[] {
    const chunkedArr = [];
    let copied = [...array];
    const numOfChild = Math.ceil(copied.length / size); // Round up to the nearest integer
    for (let i = 0; i < numOfChild; i++) {
      chunkedArr.push(copied.splice(0, size));
    }
    return chunkedArr;
  }

  onSelectionChange(event) {
    this.cleanUnit(this.units.find((unit) => unit.id === event.value));
    if (this.unit === undefined) this.cleanUnit(this.units.property_units[0]);
    this._changeDetectorRef.markForCheck();
  }

  allowAddNewColumn(event, rowIndex) {
    this.rowActionDisabled[rowIndex] = event;
  }

  private getUnitNumberFieldOrFirstValid(): string {
    const unitNumberField = this.fieldDefs.find(({ name }) => name === 'unit_number');

    if (unitNumberField) {
      return unitNumberField.name;
    }

    return this.fieldDefs[0].name;
  }
}
